import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  useCancelAssistantAction,
  useConfirmAssistantAction,
} from '@/hooks/use-assistant';
import { useToastStore } from '@/store/toastStore';
import { ConfirmationWidgetData } from '@/types/assistant';
import { WidgetCard } from './widget-card';
import { WidgetProps } from './widget-types';

/**
 * O cartão de confirmação do assistente (T-32) — **a única porta de escrita**.
 *
 * Enquanto ele está na tela, nada aconteceu: o backend guardou uma proposta e
 * está esperando. "Confirmar" manda só o id dela; o que vai ser gravado é o que
 * está escrito aqui, e não o que o modelo disse na bolha acima.
 *
 * O cartão de um turno antigo (`editable === false`) fica visível mas inerte —
 * confirmar de novo uma proposta já resolvida seria confirmar o passado.
 */
export function ConfirmationWidget({
  data,
  editable,
}: WidgetProps<ConfirmationWidgetData>) {
  const confirm = useConfirmAssistantAction();
  const cancel = useCancelAssistantAction();
  const { addToast } = useToastStore();

  const busy = confirm.isPending || cancel.isPending;
  const resolved = confirm.isSuccess || cancel.isSuccess;
  const open = editable && !resolved;

  const handleConfirm = () =>
    confirm.mutate(data.actionId, {
      onSuccess: turn => addToast(turn.message, 'success'),
      onError: () => addToast('Não deu para confirmar agora.', 'error'),
    });

  const handleCancel = () =>
    cancel.mutate(data.actionId, {
      onError: () => addToast('Não deu para cancelar agora.', 'error'),
    });

  return (
    <WidgetCard title={data.title} icon="checkmark-circle-outline">
      <View className="gap-1.5">
        {(data.lines ?? []).map((line, index) => (
          <View key={`${line}-${index}`} className="flex-row items-start gap-2">
            <View className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/50" />
            <ThemedText className="shrink text-sm text-foreground/75">
              {line}
            </ThemedText>
          </View>
        ))}
      </View>

      {open ? (
        <View className="flex-row items-center gap-2 pt-1">
          <PressableScale
            onPress={handleConfirm}
            disabled={busy}
            className="flex-1 flex-row items-center justify-center gap-2 rounded-full bg-primary py-2.5"
            style={{ opacity: busy ? 0.6 : 1 }}
          >
            {confirm.isPending ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Ionicons name="checkmark" size={16} color="#fff" />
            )}
            <ThemedText className="text-sm font-semibold text-white">
              Confirmar
            </ThemedText>
          </PressableScale>
          <PressableScale
            onPress={handleCancel}
            disabled={busy}
            className="rounded-full bg-neutral-100 px-4 py-2.5"
            style={{ opacity: busy ? 0.6 : 1 }}
          >
            <ThemedText className="text-sm font-semibold text-foreground/60">
              Cancelar
            </ThemedText>
          </PressableScale>
        </View>
      ) : (
        <ThemedText className="text-xs text-foreground/45">
          {confirm.isSuccess
            ? 'Feito.'
            : cancel.isSuccess
              ? 'Cancelado.'
              : 'Este pedido já foi respondido.'}
        </ThemedText>
      )}
    </WidgetCard>
  );
}
